import { IIndirectTaxParsedFrequency } from '@commenda/api/indirect-tax/registrations/types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { SalesTaxFilingFrequencyEnum } from '@prisma/client';

const FREQUENCY_LABELS: Record<string, string> = {
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  QUARTERLY_PREPAY_MONTHLY: 'Quarterly (prepay monthly)',
  SEMI_ANNUALLY: 'Semi-annually',
  ANNUAL_CALENDAR_YEAR: 'Annually',
  MONTHLY_ACCELERATED_PREPAY_EARLY: 'Monthly (accelerated prepay, early)',
  FISCAL_YEAR: 'Fiscal year',
  ANNUAL_RECONCILIATION: 'Annual reconciliation',
  BI_MONTHLY: 'Bi-monthly',
  TRI_ANNUALLY: 'Tri-annually',
  QUARTERLY_FEB: 'Quarterly (Feb start)',
  QUARTERLY_MAR: 'Quarterly (Mar start)',
};

function labelFor(freq: string): string {
  return FREQUENCY_LABELS[freq] ?? freq;
}

export interface IndirectTaxFilingFrequencyComboOption {
  id: string;
  label: string;
  frequencies: SalesTaxFilingFrequencyEnum[];
}

// Walks the backend's structured frequencies[] ({ primary, related? }) into a
// flat list of selectable options:
//   - Pass A: every distinct frequency (primary or related.value) as a single
//     option on its own.
//   - Pass B: for each entry with a non-empty `related`, a combined option
//     `primary + related[0] + related[1] + ...`.
// Zero client-side combination logic — the backend content structure decides
// which combos exist.
export function buildFrequencyComboOptions(
  frequencyOptions: IIndirectTaxParsedFrequency[]
): IndirectTaxFilingFrequencyComboOption[] {
  if (!frequencyOptions?.length) return [];

  const seenIndividual = new Set<string>();
  const individualOptions: IndirectTaxFilingFrequencyComboOption[] = [];

  const addIndividual = (freq: string) => {
    if (seenIndividual.has(freq)) return;
    seenIndividual.add(freq);
    individualOptions.push({
      id: freq,
      label: labelFor(freq),
      // Backend frequency strings are the same wire values as the Prisma
      // enum; the frontend Prisma client occasionally lags on newly-added
      // variants, so we cast here with a comment rather than gate on a
      // constant list that will drift.
      frequencies: [freq as SalesTaxFilingFrequencyEnum],
    });
  };

  frequencyOptions.forEach((entry) => {
    addIndividual(entry.primary);
    (entry.related ?? []).forEach((r) => addIndividual(r.value));
  });

  const combinedOptions: IndirectTaxFilingFrequencyComboOption[] =
    frequencyOptions
      .filter((entry) => (entry.related ?? []).length > 0)
      .map((entry) => {
        const relatedValues = (entry.related ?? []).map((r) => r.value);
        const ids = [entry.primary, ...relatedValues];
        return {
          id: ids.join('__PLUS__'),
          label: ids.map(labelFor).join(' + '),
          // Same backend-wire-vs-generated-Prisma caveat as above; the wire
          // values are the enum values.
          frequencies: ids as SalesTaxFilingFrequencyEnum[],
        };
      });

  return [...individualOptions, ...combinedOptions];
}

interface IndirectTaxFilingFrequencyCombinationSelectorProps {
  frequencyOptions: IIndirectTaxParsedFrequency[];
  value: string;
  onChange: (
    id: string,
    frequencies: SalesTaxFilingFrequencyEnum[]
  ) => void;
  disabled?: boolean;
  error?: string;
}

const IndirectTaxFilingFrequencyCombinationSelector = ({
  frequencyOptions,
  value,
  onChange,
  disabled = false,
  error,
}: IndirectTaxFilingFrequencyCombinationSelectorProps) => {
  const options = buildFrequencyComboOptions(frequencyOptions);

  const handleChange = (id: string) => {
    const match = options.find((o) => o.id === id);
    onChange(id, match?.frequencies ?? []);
  };

  const placeholder =
    options.length === 0
      ? 'Select a registration first'
      : 'Use registration default';

  return (
    <div className="flex flex-col gap-sm">
      <div className="flex items-center gap-xs">
        <span className="txt-small-bold text-text-primary">
          Filing frequency
        </span>
        <span className="txt-extra-small-regular text-text-tertiary">
          (optional)
        </span>
      </div>
      <Select
        value={value}
        onValueChange={handleChange}
        disabled={disabled || options.length === 0}
      >
        <SelectTrigger className="h-11 px-md">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.id} value={opt.id}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error ? (
        <span className="txt-extra-small-regular text-text-error">{error}</span>
      ) : null}
    </div>
  );
};

export { IndirectTaxFilingFrequencyCombinationSelector };
